import iso3166 from "iso-3166-2";

type IsoSubdivision = { name?: string } | string;
type IsoCountryData = {
  name?: string;
  sub?: Record<string, IsoSubdivision>;
  subdivisions?: Record<string, IsoSubdivision>;
  regions?: Record<string, IsoSubdivision>;
};

export type RegionOption = {
  value: string;
  label: string;
  countryCode: string;
  countryName: string;
  regionName: string;
  /**
   * `iso` codes come from the bundled ISO 3166-2 list, `supplemental` codes are
   * current ones it predates, and `undetected` is the country's `XX-Unknown`.
   */
  source: "iso" | "supplemental" | "undetected";
};

export type CountryRegionOption = {
  code: string;
  name: string;
  regions: RegionOption[];
};

export type QuickSelectGroup = {
  id: string;
  label: string;
  countryCodes: string[];
};

export const QUICK_SELECT_GROUPS: QuickSelectGroup[] = [
  {
    id: "eu",
    label: "EU",
    countryCodes: [
      "AT",
      "BE",
      "BG",
      "HR",
      "CY",
      "CZ",
      "DK",
      "EE",
      "FI",
      "FR",
      "DE",
      "GR",
      "HU",
      "IE",
      "IT",
      "LV",
      "LT",
      "LU",
      "MT",
      "NL",
      "PL",
      "PT",
      "RO",
      "SK",
      "SI",
      "ES",
      "SE",
    ],
  },
  {
    id: "north-america",
    label: "North America",
    countryCodes: ["US", "CA", "MX"],
  },
  {
    id: "latam",
    label: "LATAM",
    countryCodes: [
      "AR",
      "BO",
      "BR",
      "CL",
      "CO",
      "CR",
      "CU",
      "DO",
      "EC",
      "SV",
      "GT",
      "HN",
      "MX",
      "NI",
      "PA",
      "PY",
      "PE",
      "PR",
      "UY",
      "VE",
    ],
  },
  {
    id: "apac",
    label: "APAC",
    countryCodes: [
      "AU",
      "NZ",
      "JP",
      "CN",
      "IN",
      "SG",
      "KR",
      "HK",
      "TW",
      "PH",
      "TH",
      "MY",
      "ID",
      "VN",
    ],
  },
];

/**
 * Current first-level codes CloudFront can report that the bundled ISO 3166-2
 * list predates: France's 2016 regions, Czechia's 2016 and Poland's 2018
 * renumbering, Luxembourg's cantons, and newer Slovenian and Montenegrin
 * municipalities. Without them a visitor there matches no region.
 */
const SUPPLEMENTAL_SUBDIVISIONS: Record<string, Record<string, string>> = {
  FR: {
    ARA: "Auvergne-Rhône-Alpes",
    BFC: "Bourgogne-Franche-Comté",
    BRE: "Bretagne",
    CVL: "Centre-Val de Loire",
    COR: "Corse",
    "20R": "Corse",
    GES: "Grand Est",
    HDF: "Hauts-de-France",
    IDF: "Île-de-France",
    NOR: "Normandie",
    NAQ: "Nouvelle-Aquitaine",
    OCC: "Occitanie",
    PDL: "Pays de la Loire",
    PAC: "Provence-Alpes-Côte d’Azur",
  },
  CZ: {
    "10": "Praha",
    "20": "Středočeský kraj",
    "31": "Jihočeský kraj",
    "32": "Plzeňský kraj",
    "41": "Karlovarský kraj",
    "42": "Ústecký kraj",
    "51": "Liberecký kraj",
    "52": "Královéhradecký kraj",
    "53": "Pardubický kraj",
    "63": "Kraj Vysočina",
    "64": "Jihomoravský kraj",
    "71": "Olomoucký kraj",
    "72": "Zlínský kraj",
    "80": "Moravskoslezský kraj",
  },
  PL: {
    "02": "Dolnośląskie",
    "04": "Kujawsko-pomorskie",
    "06": "Lubelskie",
    "08": "Lubuskie",
    "10": "Łódzkie",
    "12": "Małopolskie",
    "14": "Mazowieckie",
    "16": "Opolskie",
    "18": "Podkarpackie",
    "20": "Podlaskie",
    "22": "Pomorskie",
    "24": "Śląskie",
    "26": "Świętokrzyskie",
    "28": "Warmińsko-mazurskie",
    "30": "Wielkopolskie",
    "32": "Zachodniopomorskie",
  },
  LU: {
    CA: "Capellen",
    CL: "Clervaux",
    DI: "Diekirch",
    EC: "Echternach",
    ES: "Esch-sur-Alzette",
    GR: "Grevenmacher",
    LU: "Luxembourg",
    ME: "Mersch",
    RD: "Redange",
    RM: "Remich",
    VD: "Vianden",
    WI: "Wiltz",
  },
  SI: { "212": "Mirna", "213": "Ankaran" },
  ME: { "24": "Tuzi", "25": "Zeta" },
};

/**
 * Old ISO codes whose current replacement is spelled differently, keyed by
 * country, so each pair sorts together and both rows show their code.
 */
const RENAMED_SUBDIVISIONS: Record<string, Record<string, string>> = {
  FR: { R: "PDL", U: "PAC" },
  CZ: { PR: "10", VY: "63" },
};

/** The consent geolocation endpoint's region when CloudFront sends none. */
export const undetectedRegionValue = (countryCode: string) =>
  `${countryCode}-Unknown`;
/** The endpoint's value when CloudFront can't detect the country either. */
export const UNDETECTED_COUNTRY_REGION_VALUE = undetectedRegionValue("US");
const UNDETECTED_REGION_NAME = "Region not detected";
const UNDETECTED_COUNTRY_NAME = "Region or country not detected";

/**
 * Bulk selection skips US-Unknown. CloudFront reports a visitor whose country
 * it can't detect as US-Unknown, so selecting every US row would also apply
 * that rule to those visitors.
 */
export const isBulkSelectable = (region: RegionOption) =>
  region.value !== UNDETECTED_COUNTRY_REGION_VALUE;

const COUNTRY_NAME_OVERRIDES: Record<string, string> = {
  GB: "United Kingdom",
  US: "United States",
};

const getSubdivisionName = (subdivision: IsoSubdivision, code: string) => {
  if (typeof subdivision === "string") return subdivision;
  if (subdivision?.name) return subdivision.name;
  return code;
};

const getCountrySubdivisions = (countryData?: IsoCountryData) => {
  if (!countryData) return {};
  const subdivisions =
    countryData.sub ?? countryData.subdivisions ?? countryData.regions;
  return subdivisions as Record<string, IsoSubdivision>;
};

const buildCountryRegions = (): CountryRegionOption[] => {
  const countryEntries = Object.entries(
    iso3166.data as Record<string, IsoCountryData>,
  );
  const countries = countryEntries
    .map(([code, data]) => {
      const subdivisions = getCountrySubdivisions(data);
      const regionEntries = Object.entries(subdivisions);
      if (!regionEntries.length) return null;

      const countryName = COUNTRY_NAME_OVERRIDES[code] ?? data?.name ?? code;

      const option = (
        value: string,
        regionName: string,
        source: RegionOption["source"],
      ): RegionOption => ({
        value,
        regionName,
        countryCode: code,
        countryName,
        label: `${regionName}, ${code}`,
        source,
      });
      const isoRegions = regionEntries.map(([regionCode, regionData]) => ({
        value: regionCode,
        name: getSubdivisionName(regionData, regionCode),
        source: "iso" as const,
      }));
      const isoValues = new Set(isoRegions.map((region) => region.value));
      const supplementalRegions = Object.entries(
        SUPPLEMENTAL_SUBDIVISIONS[code] ?? {},
      )
        .map(([subdivision, name]) => ({
          value: `${code}-${subdivision}`,
          name,
          source: "supplemental" as const,
        }))
        .filter((region) => !isoValues.has(region.value));
      const renamed = RENAMED_SUBDIVISIONS[code] ?? {};
      const nameByValue = new Map<string, string>();
      for (const { value, name } of [...isoRegions, ...supplementalRegions]) {
        nameByValue.set(value, name);
      }
      const named = [...isoRegions, ...supplementalRegions].map((region) => {
        const current = renamed[region.value.slice(code.length + 1)];
        const sortName =
          (current && nameByValue.get(`${code}-${current}`)) ?? region.name;
        return { ...region, sortName };
      });
      const nameCounts = new Map<string, number>();
      for (const { sortName } of named) {
        nameCounts.set(sortName, (nameCounts.get(sortName) ?? 0) + 1);
      }

      // A renumbered region is listed under both codes, and only the current
      // (supplemental) one ever matches, so it comes first and each row
      // shows its code.
      const regions = [
        ...named
          .sort(
            (a, b) =>
              a.sortName.localeCompare(b.sortName) ||
              Number(a.source === "iso") - Number(b.source === "iso"),
          )
          .map(({ value, name, sortName, source }) =>
            option(
              value,
              (nameCounts.get(sortName) ?? 0) > 1 ? `${name} (${value})` : name,
              source,
            ),
          ),
        option(
          undetectedRegionValue(code),
          undetectedRegionValue(code) === UNDETECTED_COUNTRY_REGION_VALUE
            ? UNDETECTED_COUNTRY_NAME
            : UNDETECTED_REGION_NAME,
          "undetected",
        ),
      ];

      return {
        code,
        name: countryName,
        regions,
      };
    })
    .filter((country): country is CountryRegionOption => Boolean(country))
    .sort((a, b) => a.name.localeCompare(b.name));

  return countries;
};

export const COUNTRY_REGION_OPTIONS = buildCountryRegions();

export const REGION_MULTI_SELECT_OPTIONS = COUNTRY_REGION_OPTIONS.flatMap(
  (country) => country.regions,
);

export const REGION_OPTION_LOOKUP = new Map(
  REGION_MULTI_SELECT_OPTIONS.map((option) => [option.value, option]),
);

/** Map from region value code to human-readable label. */
export const regionLabelLookup = new Map(
  REGION_MULTI_SELECT_OPTIONS.map((option) => [option.value, option.label]),
);
